class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Back/Forward Cache fix - we need to force refresh the cart
    window.addEventListener('load', () => {
      this.forceRefresh();
    });

    /**
     * Debugging bfcache
      window.addEventListener('pageshow', (e) => {
        console.log('pageshow');
        console.log(e);
        console.log(performance.getEntriesByType('navigation')[0].type);
      });
    */
  }

  forceRefresh() {
    fetch(`${routes.cart_url}?view=_fetch&t=${Date.now()}`)
      .then((response) => response.json())
      .then((response) => {
        this.renderContents(response, false);
        if (response.cart.item_count > 0) {
          this.classList.remove('is-empty');
        }
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.updateMergeTokens();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  updateMergeTokens() {
    const today = new Date();
    let thisMonth;
    if (today.getDate() < 15) {
      thisMonth = new Date(new Date().setDate(15));
      thisMonth = new Date(thisMonth.setMonth(thisMonth.getMonth() - 1));
    } else {
      thisMonth = new Date(new Date().setDate(15));
    }

    const lineItemMessages = this.querySelectorAll('.cart-item__message');
    lineItemMessages.forEach((el) => {
      const billingMonths = parseInt(el.dataset.billingMonths || '1');
      // We only want to show the upcoming renewal message cards between the 7th and 14th of the month
      // Additionally, we only want to show the upcoming renewal message for monthly subscriptions, since others won't have an immediate renewal
      const showMessageCards = today.getDate() > 6 && today.getDate() < 15 && billingMonths === 1;
      // Some new clubs don't renew on the first 15th, so we check the metaobject to determine if this month's renewal is skipped
      const skippedRenewalDate = el.dataset.skippedRenewalDate;
      const skippedThisMonth = skippedRenewalDate && new Date(skippedRenewalDate).getMonth() === today.getMonth() && new Date(skippedRenewalDate).getFullYear() === today.getFullYear()
      if (el.parentElement.parentElement.classList.contains('cart-message-card')) {
        if (!skippedThisMonth) {
          showMessageCards ? el.parentElement.parentElement.removeAttribute('hidden') : el.parentElement.parentElement.setAttribute('hidden', true);
        }
      }
      
      const nextBillingMonth = new Date(new Date(thisMonth).setMonth(thisMonth.getMonth() + billingMonths + (skippedThisMonth ? 1 : 0)));
      el.querySelectorAll('.token.next-billing-month').forEach((token) => {
        token.textContent = nextBillingMonth.toLocaleString('default', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      });
      const daysUntilNextBilling = Math.floor((nextBillingMonth - today) / (1000 * 60 * 60 * 24));
      el.querySelectorAll('.token.days-until-next-billing').forEach(token => {
        token.textContent = `${daysUntilNextBilling} ${daysUntilNextBilling === 1 ? 'day' : 'days'}`;
      });
      el.querySelectorAll('.token.this-month-name').forEach(token => {
        token.textContent = new Date(new Date().setMonth(thisMonth.getMonth() + 1)).toLocaleString('default', {month: 'long'})
      });
      el.querySelectorAll('.token.next-month-name').forEach(token => {
        token.textContent = new Date(new Date().setMonth(thisMonth.getMonth() + 2)).toLocaleString('default', {month: 'long'})
      });
    });
  }

  renderContents(parsedState, open = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.updateMergeTokens();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (open) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
